import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../reduxStore/store";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../reduxStore/hooks";
import {
  AlertColor,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack
} from "@mui/material";
import MedicalWisdomSnackbar from "../../components/MedicalWisdomSnackbar";
import { useForm } from "react-hook-form";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import mw from "../../assets/images/logo/mw.png";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { LOGIN_SIGN_IN_SUB_TITLE } from "../../constants/staticText";
import { JwtTokenAuthBody } from "../../reduxStore/login/Model";
import { jwtLogin } from "../../reduxStore/login/loginSlice";
import { useEffect } from "react";
import { userLoginDetails } from "../../reduxStore/login/userLoginDetails/userLoginDetailsSlice";
import { LoginBody } from "../../reduxStore/login/userLoginDetails/Model";


interface IFormInput {
  username: string;
  password: string;
}

// yup schema
const schema = yup.object().shape({
  username: yup.string().required("Username is a required field"),
  password: yup.string().required("Password is a required field")
});

export default function SignInView() {
  const dispatch = useDispatch<AppDispatch>();
  let navigate = useNavigate();
  const token = localStorage.getItem("access");
  const [loginMsgType, setLoginMsgType] = React.useState<AlertColor>("success");
  const [showPassword, setShowPassword] = React.useState(false);
  const [loginMsg, setLoginMsg] = React.useState("");
  // Call Login store
  const loginDetails = useAppSelector((state) => state.loginDetails);
  const userDetails = useAppSelector((state) => state.userLoginDetailsRes);


  const handleClickOpenMedicalWisdom = () => {
    console.log("medicalWISDOM ARR Viewer");
  };

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<IFormInput>({ resolver: yupResolver(schema) });

  // login form submit
  const onSubmit = (data: IFormInput) => {
    // console.log(data);
    // navigate("/audit-list");
    const loginBody = {
      username: data.username,
      password: data.password
    } as JwtTokenAuthBody;
    dispatch(jwtLogin(loginBody));
  };

  useEffect(() => {
    if (loginDetails?.response?.access !== undefined) {
      setLoginMsgType("success");
      setLoginMsg(loginDetails?.successMsg);
    }
    if (loginDetails?.error !== "") {
      setLoginMsgType("error");
      setLoginMsg(loginDetails?.error);
    }
  }, [loginDetails]);

  useEffect(() => {
    if (token !== null) {
      dispatch(
        userLoginDetails({
          access: token
        } as LoginBody)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (userDetails?.response?.user !== undefined) {
      //navigate to audit list page
      navigate('/audit-list');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails]);

  const navigateToForgotPassword = () => {
    navigate("/forgot-password");
  };

  const closeLoginSnackbar = () => {
    setLoginMsg("");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignSelf: "center",
        gap: 1
      }}
    >
      <Box>
        <img
          src={mw}
          width="250"
          height="42"
          style={{
            objectFit: "cover",
            objectPosition: "left"
          }}
          alt="Medical Wisdom logo"
        />
      </Box>
      <Box sx={{ py: 2 }}>
        <Typography
          variant="h4"
          className="heading"
          sx={{ padding: 0, mb: 0, lineHeight: 1 }}
        >
          Sign in
        </Typography>
        <Typography variant="subtitle1" sx={{ padding: 0, mb: 0 }}>
          {LOGIN_SIGN_IN_SUB_TITLE}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <TextField
          placeholder="Username"
          autoFocus
          required
          fullWidth
          variant="outlined"
          size="small"
          autoComplete="new-password"
          {...register("username")}
          error={Boolean(errors.username)}
          helperText={errors!.username?.message!}
          sx={{ pb: 3 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <PermIdentityIcon />
                </InputAdornment>
              )
            }
          }}
        />
        <TextField
          type={showPassword ? "text" : "password"}
          required
          fullWidth
          variant="outlined"
          placeholder="Password"
          autoComplete="new-password"
          size="small"
          {...register("password")}
          error={Boolean(errors.password)}
          helperText={errors.password?.message}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon />
                </InputAdornment>
              )
            }
          }}
        />
        <Grid
          container
          direction="row"
          sx={{
            justifyContent: "flex-end",
            alignItems: "center",
            my: 2
          }}
        >
          <Box>
            <Link
              variant="body2"
              underline="hover"
              sx={{
                alignSelf: "baseline",
                fontWeight: "normal"
              }}
              onClick={() => navigateToForgotPassword()}
              style={{ cursor: "pointer" }}
            >
              {"Forgot Password?"}
            </Link>
          </Box>
        </Grid>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="medium"
          sx={{ my: 1 }}
          style={{ textTransform: "none" }}
        >
          {loginDetails.loading === true ? (
            <Stack direction="row" spacing={1}>
              <Typography
                sx={{
                  textTransform: "none"
                }}
              >
                Sign in
              </Typography>
              <Box pl={1}>
                <CircularProgress color="inherit" size={18} />
              </Box>
            </Stack>
          ) : (
            "Sign in"
          )}
        </Button>
      </form>
      <Grid
        container
        direction="row"
        sx={{
          justifyContent: "center",
          alignItems: "center",
          my: 1
        }}
        spacing={1}
      >
        <Grid>
          <Typography>Powered by</Typography>
        </Grid>
        <Grid>
          <Link
            component="button"
            onClick={handleClickOpenMedicalWisdom}
            variant="body2"
            underline="hover"
            sx={{
              alignSelf: "baseline",
              fontWeight: "bold"
            }}
          >
            medicalWISDOM ARR Viewer
          </Link>
        </Grid>
      </Grid>
      <MedicalWisdomSnackbar
        msg={loginMsg}
        type={loginMsgType}
        alertClose={closeLoginSnackbar}
      />
    </Box>
  );
}
